import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit3, 
  Trash2, 
  Save, 
  X, 
  FileText, 
  Calendar,
  Clock
} from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { ConfirmationModal } from '../ui/ConfirmationModal';
// import { TextArea } from '../ui/TextArea';
import { HabitNote, CreateHabitNoteRequest, UpdateHabitNoteRequest } from '../../types';
import { habitNotesApiService } from '../../services/habitNotesApi';
import toast from 'react-hot-toast';

interface HabitNotesProps {
  habitId: string;
  habitName: string;
}

interface NoteFormData {
  title?: string;
  content: string;
}

export const HabitNotes: React.FC<HabitNotesProps> = ({ habitId, habitName }) => {
  const [notes, setNotes] = useState<HabitNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Form states
  const [isCreating, setIsCreating] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [formData, setFormData] = useState<NoteFormData>({ content: '' });
  const [submitting, setSubmitting] = useState(false);
  
  // Delete confirmation state
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);

  // Load notes when component mounts
  useEffect(() => {
    loadNotes();
  }, [habitId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadNotes = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedNotes = await habitNotesApiService.getHabitNotes(habitId);
      setNotes(fetchedNotes);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load notes';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNote = () => {
    setIsCreating(true);
    setFormData({ content: '' });
  };

  const handleEditNote = (note: HabitNote) => {
    setEditingNoteId(note.id);
    setFormData({ title: note.title || '', content: note.content });
  };

  const handleCancelEdit = () => {
    setIsCreating(false);
    setEditingNoteId(null);
    setFormData({ content: '' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.content.trim()) {
      toast.error('Note content is required');
      return;
    }

    if (formData.content.length > 1000) {
      toast.error('Note content cannot exceed 1000 characters');
      return;
    }

    if (formData.title && formData.title.length > 100) {
      toast.error('Note title cannot exceed 100 characters');
      return;
    }

    setSubmitting(true);

    try {
      if (isCreating) {
        // Create new note
        const request: CreateHabitNoteRequest = {
          title: formData.title?.trim() || undefined,
          content: formData.content.trim(),
        };
        
        const newNote = await habitNotesApiService.createNote(habitId, request);
        setNotes(prev => [newNote, ...prev]); // Add to beginning
        toast.success('Note created successfully');
        
        setIsCreating(false);
        setFormData({ content: '' });
      } else if (editingNoteId) {
        // Update existing note
        const request: UpdateHabitNoteRequest = {
          title: formData.title?.trim() || undefined,
          content: formData.content.trim(),
        };
        
        const updatedNote = await habitNotesApiService.updateNote(habitId, editingNoteId, request);
        setNotes(prev => prev.map(note => 
          note.id === editingNoteId ? updatedNote : note
        ));
        toast.success('Note updated successfully');
        
        setEditingNoteId(null);
        setFormData({ content: '' });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save note';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteNote = (noteId: string) => {
    setDeletingNoteId(noteId);
  };

  const handleConfirmDelete = async () => {
    if (!deletingNoteId) return;

    try {
      await habitNotesApiService.deleteNote(habitId, deletingNoteId);
      setNotes(prev => prev.filter(note => note.id !== deletingNoteId));
      toast.success('Note deleted successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete note';
      toast.error(errorMessage);
    } finally {
      setDeletingNoteId(null);
    }
  };

  const handleCancelDelete = () => {
    setDeletingNoteId(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatRelativeDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInHours * 60);
      return diffInMinutes <= 1 ? 'Just now' : `${diffInMinutes} min ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)} hour${Math.floor(diffInHours) === 1 ? '' : 's'} ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return diffInDays === 1 ? 'Yesterday' : `${diffInDays} days ago`;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Notes
          </h3>
        </div>
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="card p-4 animate-pulse">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-2"></div>
              <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Notes
          </h3>
        </div>
        <div className="card p-6 text-center">
          <FileText className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <Button onClick={loadNotes} variant="outline" size="sm">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Notes
        </h3>
        <Button
          onClick={handleCreateNote}
          leftIcon={<Plus size={16} />}
          size="sm"
          disabled={isCreating || editingNoteId !== null}
        >
          Add Note
        </Button>
      </div>

      {/* Create Note Form */}
      {isCreating && (
        <div className="card p-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                label="Title (optional)"
                placeholder="Note title..."
                value={formData.title || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                maxLength={100}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Content
              </label>
              <textarea
                placeholder="Write your note here..."
                value={formData.content}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                required
                rows={4}
                maxLength={1000}
                className="input w-full resize-y min-h-[80px]"
              />
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {formData.content.length}/1000 characters
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                type="submit"
                leftIcon={<Save size={16} />}
                size="sm"
                disabled={submitting || !formData.content.trim()}
                isLoading={submitting}
              >
                Save Note
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleCancelEdit}
                leftIcon={<X size={16} />}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Notes List */}
      <div className="space-y-4">
        {notes.length === 0 ? (
          <div className="card p-8 text-center">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400 mb-2">No notes yet</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mb-4">
              Add notes to track your thoughts, insights, or progress with this habit
            </p>
            <Button
              onClick={handleCreateNote}
              leftIcon={<Plus size={16} />}
              size="sm"
              variant="outline"
            >
              Add Your First Note
            </Button>
          </div>
        ) : (
          notes.map((note) => (
            <div key={note.id} className="card p-4">
              {editingNoteId === note.id ? (
                // Edit Form
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Input
                      label="Title (optional)"
                      placeholder="Note title..."
                      value={formData.title || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Content
                    </label>
                    <textarea
                      placeholder="Write your note here..."
                      value={formData.content}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                      required
                      rows={4}
                      maxLength={1000}
                      className="input w-full resize-y min-h-[80px]"
                    />
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {formData.content.length}/1000 characters
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      leftIcon={<Save size={16} />}
                      size="sm"
                      disabled={submitting || !formData.content.trim()}
                      isLoading={submitting}
                    >
                      Save Changes
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleCancelEdit}
                      leftIcon={<X size={16} />}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              ) : (
                // Display Note
                <div>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      {note.title && (
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1 break-words">
                          {note.title}
                        </h4>
                      )}
                      <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                        <div className="flex items-center space-x-1">
                          <Calendar size={12} />
                          <span>{formatRelativeDate(note.createdAt)}</span>
                        </div>
                        {note.updatedAt && (
                          <div className="flex items-center space-x-1">
                            <Clock size={12} />
                            <span>Updated {formatRelativeDate(note.updatedAt)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1 ml-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditNote(note)}
                        disabled={isCreating || editingNoteId !== null}
                        className="p-1 h-8 w-8"
                      >
                        <Edit3 size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteNote(note.id)}
                        disabled={isCreating || editingNoteId !== null}
                        className="p-1 h-8 w-8 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                  <div className="mt-2">
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                      {note.content}
                    </p>
                  </div>
                  <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Created {formatDate(note.createdAt)}
                      {note.updatedAt && note.updatedAt !== note.createdAt && 
                        ` • Last updated ${formatDate(note.updatedAt)}`
                      }
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deletingNoteId !== null}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Note"
        message="Are you sure you want to delete this note? This action cannot be undone."
        confirmText="Delete Note"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
}; 